import { useEffect, useState, useCallback } from "react";
import { isMobileDevice } from "../../../utils/mobile";
import { getScrollElement } from "./ChapterScroller";

export const CHAPTER_MODES = {
  DEFAULT: "default", // traditional reading view
  AUDIO_SYNC: "audio-sync", // sync view to verse being played in audio
  AUDIO_USER_SCROLLING: "audio-user-scrolling" // audio playing and arrow pointing to verse while user scrolls around
};

// This numeric scroll locking system is implemented to allow detection
// of a user scroll vs programatic scrolling (allowing us to jump out of
// audio sync mode and into audio user scrolling mode). Scroll locks should
// be incremented for each programatic scroll that is in progress and
// decremented upon completion. Currently the UI will allow allow two scroll
// locks to be created at a given time, one through the automatic scrolling
// as the audio progressed and another if the user skips in the audio during
// the first scenario. During the transition time the user will not be able
// to scroll, however this will only be restricticted to the duration of the
// scrolling transition (which value can be referenced in ChapterScroller.js)
let scrollLocks = 0;
const scrollLockStore = {
  incrementLocks: () => scrollLocks++,
  decrementLocks: () => scrollLocks--
};

interface ChapterModeUtils {
  mode: string;
  setModeAudioSync: () => void;
  setModeDefault: () => void;
  decrementScrollLocks: () => void;
  incrementScrollLocks: () => void;
}

export const useChapterMode = (hasParallels: boolean): ChapterModeUtils => {
  const [mode, setMode] = useState(CHAPTER_MODES.DEFAULT);

  // currently we have chosen to disable auto scrolling
  // when there is one or more parallels open
  if (hasParallels && mode !== CHAPTER_MODES.DEFAULT) {
    setMode(CHAPTER_MODES.DEFAULT);
  }

  // switch to audio user scrolling mode if the user is currently
  // in audio sync mode and there are no scroll locks
  const scrollHandler = () => {
    if (mode === CHAPTER_MODES.AUDIO_SYNC && scrollLocks < 1) {
      setMode(CHAPTER_MODES.AUDIO_USER_SCROLLING);
    }
  };

  useEffect(() => {
    const scrollElement = getScrollElement(isMobileDevice());
    if (scrollElement) {
      scrollElement.addEventListener("scroll", scrollHandler);
      return () => scrollElement.removeEventListener("scroll", scrollHandler);
    }
  });

  const setModeAudioSync = useCallback(
    () => setMode(CHAPTER_MODES.AUDIO_SYNC),
    []
  );
  const setModeDefault = useCallback(() => setMode(CHAPTER_MODES.DEFAULT), []);
  const decrementScrollLocks = useCallback(
    () => scrollLockStore.decrementLocks(),
    []
  );
  const incrementScrollLocks = useCallback(
    () => scrollLockStore.incrementLocks(),
    []
  );

  return {
    mode,
    setModeAudioSync,
    setModeDefault,
    decrementScrollLocks,
    incrementScrollLocks
  };
};
